import { useMemo } from 'react';
import type {
  AssessmentDomainDetail,
  AssessmentDomainDocumentDetail,
  PreviousDomainSummary,
} from '@libs/types';

import { DI_TOKENS } from '@constants';
import { useInjection } from '@hooks';
import { ValidationHelpers } from '@libs/constants';
import { AssessmentWorkflowStore } from '@stores';

import {
  AssessmentHeaderViewModel,
  DomainDetailsViewModel,
  ExpectedDocument,
  extractExpectedDocuments,
  findDomainById,
  mapToAssessmentHeaderViewModel,
  mapToDomainDetailsViewModel,
  mapToPreviousVersionViewModel,
  mapToProgressFlowViewModel,
  PreviousVersionViewModel,
  ProgressFlowViewModel,
} from '../utils/assessment-mapper';

/**
 * Hook that maps raw API data to separate view model states
 *
 * Takes the raw API response from useGetAssessmentDomainDetails and transforms
 * it into separate, focused view models for different parts of the UI.
 *
 * @param domainDetailsData - Raw API response containing assessment and domain data
 * @returns Separate view models for header, progress flow, domain details, and documents
 *
 * @example
 * ```tsx
 * const { data: domainDetailsData } = useGetAssessmentDomainDetails(...);
 *
 * const {
 *   headerViewModel,
 *   progressFlowViewModel,
 *   domainDetailsViewModel,
 *   documents
 * } = useAssessmentWorkflowViewModels({domainDetailsData, domainIdentifier});
 * ```
 */
export const useAssessmentWorkflowViewModels = (
  { domainDetailsData, domainIdentifier }:
    {
      domainDetailsData: {
        assessmentDomain: AssessmentDomainDetail;
        previousDomainSummary?: PreviousDomainSummary;
      } | undefined
      domainIdentifier: string;
    }
) => {
  // Assessment-level data is owned by the layout and mirrored into the store
  // so any consumer (this hook, modals, child components) can read it without
  // being prop-drilled.
  const workflowStore = useInjection<AssessmentWorkflowStore>(
    DI_TOKENS.AssessmentWorkflowStore,
  );
  const assessment = workflowStore.assessment ?? undefined;
  const previousAssessmentSummary = assessment?.previousAssessmentSummary;

  /**
   * Header view model
   * Contains assessment name and creation date
   */
  const headerViewModel = useMemo<AssessmentHeaderViewModel>(() => {
    return mapToAssessmentHeaderViewModel(assessment);
  }, [assessment, domainIdentifier]);

  /**
   * Progress flow view model
   * Contains domain progression data, states, completion percentage
   */
  const progressFlowViewModel = useMemo<ProgressFlowViewModel>(() => {
    return mapToProgressFlowViewModel(assessment);
  }, [assessment, domainDetailsData?.assessmentDomain, domainIdentifier]);

  /**
   * Domain details view model
   * Contains current domain fields, requirements, and configuration
   */
  const domainDetailsViewModel = useMemo<DomainDetailsViewModel | null>(() => {
    if (!domainDetailsData?.assessmentDomain) {
      return null;
    }
    return mapToDomainDetailsViewModel(domainDetailsData.assessmentDomain);
  }, [domainDetailsData?.assessmentDomain, domainIdentifier]);

  /**
   * Raw documents array
   * Pass-through without mapping for use with useAssessmentFileOperations
   */
  const documents = useMemo<AssessmentDomainDocumentDetail[]>(() => {
    return domainDetailsData?.assessmentDomain?.documents || [];
  }, [domainDetailsData?.assessmentDomain?.documents, domainIdentifier]);

  /**
   * Expected documents for the current domain
   * Extracts unique document requirements with descriptions
   */
  const expectedDocuments = useMemo<ExpectedDocument[]>(() => {
    if (!domainDetailsViewModel?.fields) {
      return [];
    }
    return extractExpectedDocuments(domainDetailsViewModel.fields, documents);
  }, [domainDetailsViewModel, documents, domainIdentifier]);

  /**
   * Re-assessment view model
   * Centralises every parent-version derivation (version, submittedAt,
   * domain score, parent doc count) consumed by the workflow page.
   */
  const previousVersionViewModel = useMemo<PreviousVersionViewModel>(() => {
    return mapToPreviousVersionViewModel(
      previousAssessmentSummary,
      domainDetailsData?.previousDomainSummary,
      domainDetailsData?.assessmentDomain?.documents,
    );
  }, [
    previousAssessmentSummary,
    domainDetailsData?.previousDomainSummary,
    domainDetailsData?.assessmentDomain?.documents,
  ]);

  const selectedIndex = useMemo(() => {
    if (domainIdentifier) {
      if (ValidationHelpers.isUUID(domainIdentifier)) {
        const domain = findDomainById(assessment, domainIdentifier);
        const orderIndex = domain?.frameworkDomain?.orderIndex ?? 1;
        return orderIndex;
      } else {
        const numericIndex = parseInt(domainIdentifier, 10);
        return !isNaN(numericIndex) && numericIndex >= 1
          ? numericIndex
          : progressFlowViewModel.currentDomainIndex;
      }
    }

    return progressFlowViewModel.currentDomainIndex;
  }, [domainIdentifier, assessment, progressFlowViewModel.currentDomainIndex]);


  return {
    headerViewModel,
    progressFlowViewModel,
    domainDetailsViewModel,
    previousVersionViewModel,
    documents,
    expectedDocuments,
    selectedIndex,
  };
};
